import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomEntity } from 'src/app/models/customEntity.model';
import { Sample } from 'src/app/models/sample.model';
import { SampleWeatherValues, WeatherCoordinates, WeatherDailyResponse } from 'src/app/models/weather.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { TranslateService } from 'src/app/services/translate.service';
import { WEATHER_DAY_DATE_LENGTH, WEATHER_VALUE_DECIMALS } from 'src/app/shared/constants/weather.constants';
import { extractAskErrorMessage, extractRetryAfterSeconds } from 'src/app/shared/utils/ask-error.util';

@Injectable()
export class SampleWeatherService {

  constructor(
    private readonly dateFormatService: DateFormatService,
    private readonly translateService: TranslateService
  ) { }

  getCoordinates(endPoint: Pick<CustomEntity, 'latitude' | 'longitude'> | null | undefined): WeatherCoordinates | null {
    const latitude = endPoint?.latitude;
    const longitude = endPoint?.longitude;
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
      return null;
    }
    return { latitude, longitude };
  }

  getSamplingDay(startDate: Sample['startDate'] | null | undefined): Date | null {
    if (startDate instanceof Date) {
      return isNaN(startDate.getTime()) ? null : startDate;
    }
    if (typeof startDate === 'string' && startDate) {
      return this.dateFormatService.convertStringToDate(startDate) ?? null;
    }
    return null;
  }

  /**
   * Returns null when the provider has no value for any of the sample's ambient fields on that day.
   * Days arrive as UTC-midnight timestamps of the location's calendar date ("2026-10-05T00:00:00Z"), so they
   * are matched on the date part as text; parsing them would shift the day in timezones west of UTC.
   */
  toSampleValues(response: WeatherDailyResponse | null, requestDate: string): SampleWeatherValues | null {
    const day = response?.days?.find(d => d.date?.slice(0, WEATHER_DAY_DATE_LENGTH) === requestDate);
    if (!day) {
      return null;
    }
    const values: SampleWeatherValues = {};
    if (day.temperatureMinC != null) values.temperatureMinC = this.roundTemperature(day.temperatureMinC);
    if (day.temperatureMaxC != null) values.temperatureMaxC = this.roundTemperature(day.temperatureMaxC);
    if (day.humidityPercent != null) values.humidityPercent = day.humidityPercent;
    if (day.pressureMmhg != null) values.pressureMmhg = day.pressureMmhg;
    return Object.keys(values).length ? values : null;
  }

  isComplete(values: SampleWeatherValues): boolean {
    return values.temperatureMinC != null && values.temperatureMaxC != null
      && values.humidityPercent != null && values.pressureMmhg != null;
  }

  wouldOverwrite(entity: Sample, values: SampleWeatherValues): boolean {
    return (values.temperatureMinC != null && entity.temperatureMinC != null && entity.temperatureMinC !== values.temperatureMinC)
      || (values.temperatureMaxC != null && entity.temperatureMaxC != null && entity.temperatureMaxC !== values.temperatureMaxC)
      || (values.humidityPercent != null && entity.humidityPercent != null && entity.humidityPercent !== values.humidityPercent)
      || (values.pressureMmhg != null && entity.pressureMmhg != null && entity.pressureMmhg !== values.pressureMmhg);
  }

  isNoDataError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound;
  }

  getErrorMessage(error: unknown): string {
    const status = error instanceof HttpErrorResponse ? error.status : null;
    switch (status) {
      case HttpStatusCode.BadRequest:
        return extractAskErrorMessage(error)
          ?? this.translateService.getDynamicTranslation('The location or date of this sample cannot be used to look up weather data.');
      case HttpStatusCode.Forbidden:
        return this.translateService.getDynamicTranslation('You do not have permission to fetch weather data.');
      case HttpStatusCode.TooManyRequests: {
        const seconds = extractRetryAfterSeconds(error);
        return seconds != null
          ? this.translateService.getDynamicTranslation('The weather service is busy. Try again in {{seconds}} seconds.', { seconds })
          : this.translateService.getDynamicTranslation('The weather service is busy. Try again later.');
      }
      case HttpStatusCode.BadGateway:
        return this.translateService.getDynamicTranslation('The weather service is unavailable. Try again later.');
      case HttpStatusCode.GatewayTimeout:
        return this.translateService.getDynamicTranslation('The weather service did not respond in time. Try again.');
      default:
        return extractAskErrorMessage(error) ?? this.translateService.getDynamicTranslation('Weather data could not be fetched.');
    }
  }

  private roundTemperature(value: number): number {
    const factor = Math.pow(10, WEATHER_VALUE_DECIMALS);
    return Math.round(value * factor) / factor;
  }
}
